import { useRef } from 'react';
import alert from '@components/alert';
import useConcurrentEditLock from './useConcurrentEditLock';
import { NETWORK_ERROR } from '@model';
import useIdleTimer from './useIdleTimer';
import { useSelector } from 'react-redux';

function useConcurrentEditAreaMonitor({ selector, identifier, cancelEdit }) {
  const { releaseLock, releaseLockBeforeUnload, sendQuery } =
    useConcurrentEditLock({ auto: false });
  const extendAbortController = useRef(null);
  const userInfo = useSelector(({ login }) => login.userInfo);

  const editTimeout = () => {
    alert.warning({
      className: 'edit-lock-warning',
      content:
        'Hi there! Your editing session has been timed out after 20 minutes of inactivity.',
      onOk: cancelEdit,
      onCancel: cancelEdit,
    });
    if (extendAbortController.current) {
      extendAbortController.current.abort();
    }
    releaseLock({
      moduleIdentifier: identifier,
      userEmail: userInfo.email,
      userName: userInfo.name,
    });
  };

  const { extendValidity } = useConcurrentEditLock({ auto: false });
  const onTimerRefresh = () => {
    extendAbortController.current = new AbortController();
    extendValidity(
      {
        moduleIdentifier: identifier,
        userEmail: userInfo?.email,
      },
      {
        signal: extendAbortController?.current.signal,
      },
    ).then((res) => {
      if (res && !res?.extendSuccessful) {
        alert.error({
          className: 'edit-lock-warning',
          content: res?.message || NETWORK_ERROR.INTERNAL_SERVER_ERROR,
          onOk: cancelEdit,
          onCancel: cancelEdit,
        });
      }
    });
  };

  useIdleTimer({
    selector,
    onTimeout: editTimeout,
    onRefresh: onTimerRefresh,
    events: ['click', 'input'],
    // timeout: 30 * 1000,
  });

  return {
    extendAbortController,
    releaseLock: () =>
      releaseLock({
        moduleIdentifier: identifier,
        userEmail: userInfo.email,
        userName: userInfo.name,
      }),
    queryLockInfo: sendQuery,
    releaseLockBeforeUnload() {
      return releaseLockBeforeUnload({
        moduleIdentifier: identifier,
        userEmail: userInfo.email,
        userName: userInfo.name,
      });
    },
  };
}

export default useConcurrentEditAreaMonitor;
